import { useCallback, useEffect, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { FormProvider, useForm } from 'react-hook-form';
import { OnboardingFormData } from './FYIGroupsOnboarding';
import { FYIGroup } from '../../../../../../../types/fyi-docs';
import { ReactComponent as Empty } from '../../../../../../../assets/empty.svg';
import { CreateFYIGroupsResponse } from '../../../../../../../queries/use-create-fyi-groups';
import { SimpleAlert, SortOrder, Spinner } from '@cimplico/components';
import SubmitButton from './SubmitButton';
import FYIGroupRow from './FYIGroupRow';
import GlobalControls from './GlobalControls';
import EmptyStateNew from '../../../../../../../components/empty-state/EmptyStateNew';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

const COLLAPSED_GROUP_ROW_HEIGHT = 60;
const EXPANDED_GROUP_ROW_HEIGHT = COLLAPSED_GROUP_ROW_HEIGHT * 2;

type FYIGroupsListProps = {
    showOnboarded: boolean;
    fyiGroups: FYIGroup[];
    fullPage?: boolean;
    search?: string;
    sort?: { order: SortOrder; option?: string | undefined };
    onClearSearch: () => void;
    onSetAlert: (alert?: { errors: { [groupName: string]: string[] }; success: CreateFYIGroupsResponse }) => void;
};

function FYIGroupsList({
    showOnboarded,
    fyiGroups,
    fullPage = false,
    search = '',
    sort,
    onSetAlert,
    onClearSearch,
}: FYIGroupsListProps): JSX.Element {
    const [progress, setProgress] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<OnboardingFormData>({
        defaultValues: {
            data: fyiGroups.map((g) => ({
                group: {
                    fyiGroup: g,
                    isSelected: false,
                    isExpanded: false,
                    hasExpandedEntities: false,
                },
                entities: [],
            })),
            globalSelected: false,
        },
    });

    const formData = form.getValues('data');
    const sizes = useRef<number[]>(new Array(formData.length).fill(COLLAPSED_GROUP_ROW_HEIGHT));
    const listRef = useRef<VariableSizeList | null>(null);

    const filteredData = formData
        .filter((d) => (showOnboarded || search ? true : !d.group.fyiGroup.existingMapping))
        .filter((d) => d.group?.fyiGroup?.name?.toLowerCase()?.includes(search.toLowerCase()));

    const getRowSize = useCallback((index: number) => {
        return sizes.current[index] || COLLAPSED_GROUP_ROW_HEIGHT;
    }, []);

    const expandRow = useCallback((expand: boolean, index?: number) => {
        // // Expand all
        if (index === undefined) {
            sizes.current = new Array(formData.length).fill(expand ? EXPANDED_GROUP_ROW_HEIGHT : COLLAPSED_GROUP_ROW_HEIGHT);
        }
    }, []);

    const changeRowSize = useCallback((index: number, size?: number) => {
        // Change size
        if (sizes.current[index] === size || size === undefined) {
            return;
        }

        sizes.current[index] = size;
        listRef.current?.resetAfterIndex(index || 0);
    }, []);

    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [search, sort]);

    // onSubmit, refetch FYI groups and update default values
    useEffect(() => {
        const vals = form.getValues();
        form.reset({
            data: fyiGroups.map((g) => {
                const prev = vals.data.find((d) => d.group.fyiGroup.id === g.id);
                return {
                    group: {
                        fyiGroup: g,
                        isSelected: prev?.group.isSelected || false,
                        isExpanded: prev?.group.isExpanded || false,
                        hasExpandedEntities: prev?.group.hasExpandedEntities || false,
                        month: prev?.group.month,
                        day: prev?.group.day,
                        monthError: prev?.group.monthError,
                        dayError: prev?.group.dayError,
                    },
                    entities: [],
                };
            }),
            globalSelected: false,
        });
    }, [fyiGroups, showOnboarded, search, form]);

    if (formData.filter((d) => d.group?.fyiGroup?.name?.toLowerCase().includes(search.toLowerCase())).length === 0) {
        return (
            <div className="flex flex-col items-center mt-12">
                <Empty className="mb-4" />
                <p className="text-center text-base text-gray-600">No groups match the name &quot;{search}&quot;</p>
            </div>
        );
    }

    return (
        <FormProvider {...form}>
            {formData.some((d) => d.group.monthError || d.group.dayError) && (
                <SimpleAlert
                    type="error"
                    title="Please ensure all selected groups have a configured financial year end month and day so their entities can be onboarded."
                />
            )}
            <form>
                <fieldset disabled={isSubmitting}>
                    {!isSubmitting && <GlobalControls onExpand={expandRow} search={search} showOnboarded={showOnboarded} />}
                    {isSubmitting && (
                        <div className="mt-4 py-[7px] flex items-center gap-4 text-sm text-gray-800">
                            <p>Creating groups</p>
                            <Spinner size="sm" />
                            <p className="text-gray-600">
                                {progress} / {total} ({Math.round((progress / total) * 100)}%)
                            </p>
                        </div>
                    )}

                    {filteredData.length === 0 ? (
                        <div className="my-32">
                            <EmptyStateNew
                                title="No FYI Groups"
                                helpText="All groups may have already been onboarded. You can show onboarded groups using the filter above."
                                icon={<BuildingOffice2Icon />}
                            />
                        </div>
                    ) : (
                        <VariableSizeList
                            height={fullPage ? 550 : 400}
                            width={'100%'}
                            itemCount={filteredData.length}
                            itemSize={getRowSize}
                            itemData={filteredData.sort((a, b) =>
                                a.group.fyiGroup.name > b.group.fyiGroup.name && sort?.order === 'desc' && sort?.option === 'name' ? -1 : 1
                            )}
                            ref={listRef}
                            className="mt-4"
                            useIsScrolling
                        >
                            {({ style, index, data, isScrolling }): JSX.Element => {
                                if (data.length === 0) {
                                    data.length === 0 && <p>Empty</p>;
                                }
                                return (
                                    <div style={style}>
                                        <FYIGroupRow
                                            listIndex={index}
                                            isScrolling={isScrolling}
                                            key={data[index].group.fyiGroup.id}
                                            index={formData.findIndex((d) => d.group.fyiGroup.id === data[index].group.fyiGroup.id)}
                                            onExpand={expandRow}
                                            onSizeChange={changeRowSize}
                                        />
                                    </div>
                                );
                            }}
                        </VariableSizeList>
                    )}
                    <div className="flex justify-between pt-4 items-center">
                        <p className="text-sm">
                            Showing{' '}
                            {
                                formData
                                    .filter((d) => (showOnboarded || search ? true : !d.group.fyiGroup.existingMapping))
                                    .filter((d) => d.group?.fyiGroup?.name?.toLowerCase()?.includes(search.toLowerCase())).length
                            }{' '}
                            out of {formData.length} results
                        </p>
                        <SubmitButton
                            onSetAlert={onSetAlert}
                            onProgressChange={(p): void => (p === 0 ? setProgress(0) : setProgress((prev) => prev + p))}
                            onTotalChange={setTotal}
                            onSubmit={setIsSubmitting}
                            onClearSearch={onClearSearch}
                        />
                    </div>
                </fieldset>
            </form>
        </FormProvider>
    );
}

export default FYIGroupsList;
